import React, { useState, useMemo, useCallback, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Button, Typography, FormControl, Select, MenuItem, Alert, Box, Chip, TableSortLabel, OutlinedInput, InputAdornment } from "@mui/material";
import Checkbox from '@mui/material/Checkbox';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { TableVirtuoso } from 'react-virtuoso';

// Определение столбцов таблицы
const columns = [
    {
        id: 'checkbox', label: '', minWidth: 40, width: 40,
        align: 'center',
    },
    {
        id: 'location', label: '', minWidth: 40, width: 40,
        align: 'center',
    },
    {
        id: 'object1', label: 'Линия от', minWidth: 120, width: 150,
        align: 'center',
    },
    {
        id: 'object2', label: 'Лнния до', minWidth: 120, width: 150,
        align: 'center',
    },
    // {
    //     id: 'quantity', label: 'Количество цепей', minWidth: 100, width: 120,
    //     align: 'center',
    // },
    {
        id: 'distance', label: 'Смонтировано провода', minWidth: 100, width: 120,
        align: 'center',
    },
    {
        id: 'totalLength', label: 'Общая длина провода', minWidth: 120, width: 150,
        align: 'center',
    },
    {
        id: 'specification', label: 'Спецификация', minWidth: 200, width: 250,
        align: 'center',
    },
];

// Стили для таблицы
const StyledTableCellSpec = styled(TableCell)(({ theme, darkMode }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: darkMode ? '#424242' : '#D0D0D0',
        color: darkMode ? '#f5f7fa' : 'black',
        padding: '4px 4px',
        fontSize: '16px',
        fontWeight: 'bold',
        '&:hover': {
            backgroundColor: darkMode ? '#616161' : '#909090',
            cursor: 'pointer'
        },
        '@media (max-width: 768px)': {
            fontSize: '12px',
            padding: '1px 2px',
        }
    },
    [`&.${tableCellClasses.body}`]: {
        padding: '2px 4px',
        fontSize: 14,
        color: darkMode ? '#f5f7fa' : 'inherit',
        '@media (max-width: 768px)': {
            fontSize: '12px',
            padding: '1px 2px',
        }
    },
}));

const StyledTableCellSpecCompact = styled(TableCell)(({ theme, darkMode }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: darkMode ? '#424242' : '#D0D0D0',
        color: darkMode ? '#f5f7fa' : 'black',
        padding: '2px 2px',
        fontSize: '14px',
        fontWeight: 'bold',
        '&:hover': {
            backgroundColor: darkMode ? '#616161' : '#909090',
            cursor: 'pointer'
        }
    },
    [`&.${tableCellClasses.body}`]: {
        padding: '1px 2px',
        fontSize: 12,
        color: darkMode ? '#f5f7fa' : 'inherit',
    },
}));

const StyledTableRowSpec = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'allChecked' && prop !== 'darkMode'
})(({ theme, allChecked, darkMode }) => ({
    backgroundColor: darkMode
        ? (allChecked ? '#2e4a2e' : '#4a3e2e')
        : (allChecked ? '#e8f5e9' : '#fff3e0'),
}));

const StyledTableRowSpecDisabled = styled(TableRow, {
    shouldForwardProp: (prop) => prop !== 'allChecked' && prop !== 'disabled' && prop !== 'darkMode'
})(({ theme, allChecked, disabled, darkMode, status }) => ({
    backgroundColor: disabled
        ? (darkMode ? '#2a2a2a' : '#f5f5f5')
        : darkMode
            ? (allChecked ? '#2e4a2e' : '#4a3e2e')
            : status === 1
                ? '#e8f5e9'
                : status === 2
                    ? '#FFFEE1'
                    : '#FFE1D7',
    opacity: disabled ? 0.6 : 1,
    '& > td:not(:first-of-type)': {
        pointerEvents: disabled ? 'none' : 'auto',
    },
    '& > td:not(:first-of-type) *': {
        color: disabled ? (darkMode ? '#666 !important' : '#999 !important') : 'inherit'
    }
}));

// Компоненты для виртуализации таблицы
const createVirtuosoTableComponents = (darkMode) => ({
    Scroller: React.forwardRef((props, ref) => (
        <TableContainer
            component={Paper}
            {...props}
            ref={ref}
            sx={{
                '& .MuiTable-root': {
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                },
                // Кастомные стили скролла
                '&::-webkit-scrollbar': {
                    width: '8px',
                    height: '8px',
                },
                '&::-webkit-scrollbar-track': {
                    background: darkMode ? '#2a2a2a' : '#f1f1f1',
                    borderRadius: '4px',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: darkMode ? '#555' : '#c1c1c1',
                    borderRadius: '4px',
                    '&:hover': {
                        background: darkMode ? '#777' : '#a8a8a8',
                    },
                },
                '&::-webkit-scrollbar-corner': {
                    background: darkMode ? '#2a2a2a' : '#f1f1f1',
                },
                // Для Firefox
                scrollbarWidth: 'thin',
                scrollbarColor: darkMode ? '#555 #2a2a2a' : '#c1c1c1 #f1f1f1',
            }}
        />
    )),
    Table: (props) => (
        <Table {...props} size="small" />
    ),
    TableHead: React.forwardRef((props, ref) => (
        <TableHead {...props} ref={ref} />
    )),
    TableRow: ({ item: _item, ...props }) => <TableRow {...props} />,
    TableBody: React.forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
});

const ObjectsFidersTable = React.forwardRef(({
    objectsFiders,
    setObjectsFiders,
    smallMode,
    mode,
    disabledObjects,
    setDisabledObjects,
    searchText,
    specs,
    evalMaxQuantity,
    selectObjectFider,
    selectObject,
    map,
    dialogRef,
    darkMode = false
}, ref) => {
    const addButtonRef = useRef(null);

    // Состояние для измененных данных
    const [changedObjectsFiders, setChangedObjectsFiders] = useState({});

    // Внутренние состояния для сортировки
    const [sortConfig, setSortConfig] = useState({ key: 'object1', direction: 'asc' });

    // Функция сортировки
    const handleSort = useCallback((key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    }, [sortConfig]);

    // Синхронизируем изменения с родительским компонентом
    React.useEffect(() => {
        if (Object.keys(changedObjectsFiders).length > 0) {
            // Здесь можно добавить логику для отправки изменений на сервер
            // или уведомления родительского компонента об изменениях
            console.log('Измененные фидеры:', changedObjectsFiders);
        }
    }, [changedObjectsFiders]);

    // Функция применения изменений к данным
    const applyChangesToData = useCallback((data) => {
        if (!data || Object.keys(changedObjectsFiders).length === 0) {
            return data;
        }

        return data.map(segment => {
            // Используем комбинацию fid и key для идентификации сегмента
            const segmentKey = `${segment.fid}_${segment.key}`;
            const changes = changedObjectsFiders[segmentKey];

            if (changes) {
                return { ...segment, ...changes };
            }

            return segment;
        });
    }, [changedObjectsFiders]);

    // Экспортируем метод для получения обновленных данных
    React.useImperativeHandle(ref, () => ({
        getUpdatedData: () => {
            return applyChangesToData(objectsFiders);
        },
        getChangedObjects: () => {
            return changedObjectsFiders;
        }
    }), [objectsFiders, changedObjectsFiders, applyChangesToData]);

    // Функция подготовки данных для виртуализации
    const prepareData = useCallback((fidersData) => {
        if (!fidersData || !specs) return { groupedData: [], groupCounts: [] };

        // Сохраняем исходные данные ДО применения изменений
        const originalData = fidersData.map(segment => ({
            fid: segment.fid,
            key: segment.key,
            length: segment.length,
            sip_class: segment.sip_class ? [...segment.sip_class] : [],
            progress: segment.progress,
            status: segment.status
        }));
        
        // Применяем изменения к данным
        const dataWithChanges = applyChangesToData(fidersData);
        
        // Данные уже развернуты на сегменты, просто добавляем оригинальные данные
        const expandedData = dataWithChanges.map(segment => {
            const originalSegment = originalData.find(o => o.fid === segment.fid && o.key === segment.key);
            return {
                ...segment,
                originalSegment: originalSegment || {
                    length: segment.length,
                    sip_class: segment.sip_class || [],
                    progress: segment.progress,
                    status: segment.status
                }
            };
        });
        
        let filteredData = expandedData;
        if (searchText) {
            const searchTerms = searchText.toLowerCase().trim().split(' ').filter(word => word.length > 0);

            filteredData = filteredData.filter(segment => {
                const sipClassIds = segment.sip_class || [];
                const sipClassNames = sipClassIds.map(id => {
                    const spec = specs?.find(s => s.id === id);
                    return spec?.name_str || '';
                }).join(' ');
                
                const textToSearch = (segment.fid || '') + ' ' +
                    (segment.leftObject?.name || '') + ' ' +
                    (segment.leftObject?.short_name || '') + ' ' +
                    (segment.rightObject?.name || '') + ' ' +
                    (segment.rightObject?.short_name || '') + ' ' +
                    sipClassNames;
                return searchTerms.some(term => textToSearch?.toLowerCase().includes(term))
            });
        }

        // Применяем сортировку
        if (sortConfig.key) {
            filteredData = filteredData.sort((a, b) => {
                let aValue, bValue;
                let type = 'string';

                switch (sortConfig.key) {
                    case 'object1':
                        aValue = a.leftObject?.name || '';
                        bValue = b.leftObject?.name || '';
                        break;
                    case 'object2':
                        aValue = a.rightObject?.name || '';
                        bValue = b.rightObject?.name || '';
                        break;
                    case 'distance':
                        aValue = a.length || 0;
                        bValue = b.length || 0;
                        type = 'number';
                        break;
                    case 'totalLength':
                        // Общая длина = длина сегмента × количество спецификаций
                        const aSipCount = Array.isArray(a.sip_class) ? a.sip_class.length : 1;
                        const bSipCount = Array.isArray(b.sip_class) ? b.sip_class.length : 1;
                        aValue = (a.length || 0) * aSipCount;
                        bValue = (b.length || 0) * bSipCount;
                        type = 'number';
                        break;
                    case 'name':
                        aValue = a.name || '';
                        bValue = b.name || '';
                        break;
                    default:
                        aValue = a[sortConfig.key];
                        bValue = b[sortConfig.key];
                }

                if (!aValue && !bValue) {
                    aValue = a.fid;
                    bValue = b.fid;
                    type = 'number';
                }

                if (type === 'string') {
                    return sortConfig.direction === 'asc'
                        ? aValue.localeCompare(bValue)
                        : bValue.localeCompare(aValue);
                } else {
                    if (aValue < bValue) {
                        return sortConfig.direction === 'asc' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'asc' ? 1 : -1;
                    }
                    return 0;
                }
            });
        }
        // Для фидеров мы не используем группировку, просто возвращаем данные как есть
        return {
            groupedData: filteredData,
            groupCounts: filteredData.length > 0 ? [filteredData.length] : []
        };
    }, [searchText, specs, applyChangesToData, sortConfig, changedObjectsFiders]);

    // Получаем подготовленные данные для виртуализации
    const groupedData = useMemo(() => {
        return prepareData(objectsFiders);
    }, [objectsFiders, prepareData]);

    // Мемоизируем компоненты виртуозо, чтобы избежать пересоздания и сброса скролла
    const virtuosoComponents = useMemo(() => createVirtuosoTableComponents(darkMode), [darkMode]);

    // Логика для определения состояния "выбрать все"
    const getSelectAllState = useCallback(() => {
        if (!groupedData.groupedData || groupedData.groupedData.length === 0) {
            return { checked: false, indeterminate: false };
        }

        const totalItems = groupedData.groupedData.length;
        const enabledItems = groupedData.groupedData.filter(segment => {
            const segmentKey = `${segment.fid}_${segment.key}`;
            return !disabledObjects.fiders?.find(id => id === segmentKey);
        });

        const enabledCount = enabledItems.length;
        const disabledCount = totalItems - enabledCount;

        if (disabledCount === 0) {
            // Все элементы выбраны (не отключены)
            return { checked: true, indeterminate: false };
        } else if (enabledCount === 0) {
            // Все элементы отключены
            return { checked: false, indeterminate: false };
        } else {
            // Частично выбраны
            return { checked: false, indeterminate: true };
        }
    }, [groupedData.groupedData, disabledObjects.fiders]);

    // Обработчик для массового выбора/снятия
    const handleSelectAll = useCallback((checked) => {
        if (!groupedData.groupedData || groupedData.groupedData.length === 0) {
            return;
        }

        // Получаем все отображаемые сегменты (независимо от их текущего состояния)
        const allVisibleSegmentKeys = groupedData.groupedData.map(segment => `${segment.fid}_${segment.key}`);

        if (checked) {
            // Чекбокс отмечен - выбираем все элементы (убираем из disabledObjects)
            setDisabledObjects(prev => ({
                ...prev,
                fiders: (prev.fiders || []).filter(key => !allVisibleSegmentKeys.includes(key))
            }));
        } else {
            // Чекбокс не отмечен - снимаем выбор со всех элементов (добавляем в disabledObjects)
            setDisabledObjects(prev => ({
                ...prev,
                fiders: [...(prev.fiders || []), ...allVisibleSegmentKeys.filter(key => !(prev.fiders || []).includes(key))]
            }));
        }
    }, [groupedData.groupedData, setDisabledObjects]);

    // Функция для заголовка таблицы
    const fixedHeaderContent = useCallback(() => {
        // Получаем только видимые столбцы для компактного режима
        const visibleColumnsArray = smallMode
            ? columns.filter(col => ['checkbox', 'location', 'object1', 'object2'].includes(col.id))
            : columns;

        const selectAllState = getSelectAllState();

        return (
            <TableRow>
                {visibleColumnsArray.map((column, index) => (
                    <TableCell
                        key={column.id}
                        variant="head"
                        align={column.align || 'left'}
                        style={{
                            width: column.width || column.minWidth,
                            minWidth: column.minWidth,
                            maxWidth: column.width || column.minWidth
                        }}
                        colSpan={column.colSpan}
                        sx={{
                            bgcolor: darkMode ? '#2b2b2b' : '#fff',
                            color: darkMode ? '#f5f7fa' : '#333',
                            fontWeight: 600,
                            fontSize: '0.875rem',
                            lineHeight: '1.2',
                            borderBottom: darkMode
                                ? '1px solid rgba(255, 255, 255, 0.12)'
                                : '2px solid rgba(0, 0, 0, 0.12)',
                            borderRight: darkMode
                                ? '1px solid rgba(255, 255, 255, 0.08)'
                                : '1px solid rgba(0, 0, 0, 0.08)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 10,
                            p: '4px 8px',
                        }}
                        onClick={() => {
                            // Только для сортируемых столбцов
                            if (['object1', 'object2', 'quantity', 'distance', 'totalLength'].includes(column.id)) {
                                handleSort(column.id);
                            }
                        }}
                    >
                        {column.id === 'checkbox' ? (
                            <Checkbox
                                size="small"
                                checked={selectAllState.checked}
                                indeterminate={selectAllState.indeterminate}
                                onChange={(e) => handleSelectAll(e.target.checked)}
                                sx={{
                                    padding: '2px',
                                    color: darkMode ? '#64b5f6' : '#1976d2',
                                    '&.Mui-checked': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                    '&.MuiCheckbox-indeterminate': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                    },
                                    '&:hover': {
                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                    }
                                }}
                            />
                        ) : ['object1', 'object2', 'quantity', 'distance', 'totalLength'].includes(column.id) ? (
                            <TableSortLabel
                                active={sortConfig?.key === column.id}
                                direction={sortConfig?.key === column.id ? sortConfig.direction : 'asc'}
                                onClick={() => handleSort(column.id)}
                                sx={{
                                    color: darkMode ? '#f5f7fa' : '#333',
                                    fontWeight: 600,
                                    fontSize: '0.875rem',
                                    '&:hover': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        borderRadius: '4px',
                                    },
                                    '&.Mui-active': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        fontWeight: 700,
                                    },
                                    '& .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#f5f7fa' : '#333',
                                        fontSize: '1rem',
                                        opacity: 0,
                                        transition: 'all 0.2s ease-in-out',
                                    },
                                    '&.Mui-active .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        opacity: 1,
                                    },
                                    '&:hover .MuiTableSortLabel-icon': {
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        opacity: 1,
                                    },
                                }}
                            >
                                {column.label}
                            </TableSortLabel>
                        ) : (
                            column.label
                        )}
                    </TableCell>
                ))}
            </TableRow>
        );
    }, [smallMode, sortConfig, handleSort, darkMode, getSelectAllState, handleSelectAll]);


    // Отладочная информация
    console.log('ObjectsFidersTable render:', {
        objectsFiders: objectsFiders?.length,
        specsSipClass: specs.sip_class?.length,
        groupedData: groupedData.groupedData?.length,
        searchText
    });

    // Если нет данных, показываем сообщение
    if (!groupedData.groupedData || groupedData.groupedData.length === 0) {
        return (
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '300px',
                width: '100%',
                backgroundColor: darkMode ? '#1e1e1e' : '#f5f7fa',
                borderRadius: '8px',
                border: darkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
                boxShadow: darkMode ? '0 4px 8px rgba(0, 0, 0, 0.3)' : '0 2px 4px rgba(0, 0, 0, 0.1)',
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                    textAlign: 'center',
                    padding: 3,
                }}>
                    <Box sx={{
                        width: 64,
                        height: 64,
                        borderRadius: '50%',
                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 1,
                    }}>
                        <Typography
                            variant="h3"
                            sx={{
                                color: darkMode ? '#64b5f6' : '#1976d2',
                                fontSize: '2rem',
                                fontWeight: 300,
                            }}
                        >
                            ⚡
                        </Typography>
                    </Box>
                    <Typography
                        variant="h5"
                        sx={{
                            color: darkMode ? '#e0e0e0' : '#424242',
                            fontWeight: 500,
                            marginBottom: 1,
                        }}
                    >
                        Нет данных для отображения
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: darkMode ? '#bbb' : '#666',
                            fontSize: '0.9rem',
                            maxWidth: '400px',
                            lineHeight: 1.5,
                        }}
                    >
                        Попробуйте изменить параметры поиска или фильтры для отображения данных
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', width: '100%' }}>
            <TableVirtuoso
                key={'fiders-table'}
                data={groupedData.groupedData}
                overscan={1900}
                style={{
                    backgroundColor: darkMode ? '#1e1e1e' : '#f5f7fa',
                }}
                components={virtuosoComponents}
                fixedHeaderContent={fixedHeaderContent}
                itemContent={(index) => {
                    const segment = groupedData.groupedData[index];
                    if (!segment) return null;
                    
                    const leftObject = segment.leftObject;
                    const rightObject = segment.rightObject;
                    
                    // Получаем оригинальные данные сегмента
                    const originalSegment = segment.originalSegment;
                    
                    // Получаем актуальные данные с учетом изменений
                    const segmentKey = `${segment.fid}_${segment.key}`;
                    const changes = changedObjectsFiders[segmentKey];
                    
                    // Применяем изменения к данным сегмента
                    const segmentLength = changes?.length ?? segment.length ?? 0;
                    const segmentProgress = changes?.progress ?? segment.progress ?? 1;
                    const segmentSipClass = changes?.sip_class ?? segment.sip_class ?? [];
                    const currentSegmentStatus = changes?.status ?? segment.status ?? 0;
                    
                    // Вычисляем прогресс и статус на основе длины
                    // distance - это референсное значение (расстояние между координатами)
                    const referenceDistance = segment.distance || originalSegment?.length || segment.length || 0;
                    const progress = referenceDistance > 0 
                        ? Math.round((segmentLength / referenceDistance) * 100)
                        : 100;
                    const status = progress < 98 ? 0 : progress > 102 ? 2 : 1;
                    
                    // Сохраняем referenceDistance для использования в других местах компонента
                    const originalSegmentLength = referenceDistance;
                    
                    // Определяем, является ли фидер combined
                    const isCombined = segment.combined || false;
                    
                    // Основной элемент - isDisabled (через disabledObjects.fiders)
                    // status меняется в зависимости от isDisabled и режима
                    // Используем уникальный ключ сегмента для управления отдельной строкой
                    const isSegmentDisabled = !!disabledObjects.fiders?.find(id => id === segmentKey);
                    const isDisabled = isSegmentDisabled;
                    
                    // Вычисляем ожидаемый status на основе isDisabled и режима
                    // В режиме выполнения: если галочка стоит (isDisabled = false), то status = 1, если снята (isDisabled = true), то status = 0
                    // В режиме отмены выполнения: если галочка стоит (isDisabled = false), то status = 0, если снята (isDisabled = true), то status = 1
                    const expectedStatus = mode 
                        ? (isSegmentDisabled ? 0 : 1)  // Режим выполнения: disabled = 0, enabled = 1
                        : (isSegmentDisabled ? 1 : 0); // Режим отмены: disabled = 1, enabled = 0
                    
                    // Используем текущий status, если он есть, иначе ожидаемый
                    const displayStatus = currentSegmentStatus ?? expectedStatus;
                    // const combined = object1?.type?.includes('pillar04-10') && object2?.type?.includes('pillar04-10');
                    // console.log(f, status);

                    // Общий стиль для всех TableCell
                    const cellStyle = {
                        minHeight: '40px',
                        height: 'auto',
                        padding: '4px 8px',
                        fontSize: '0.75rem',
                        color: darkMode ? '#f5f7fa' : '#333',
                        borderBottom: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                        borderRight: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)',
                        borderLeft: isDisabled
                            ? (darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.08)')
                            : darkMode
                                ? (status === 1
                                    ? '4px solid #4caf50'
                                    : status === 2
                                        ? '4px solid #ff9800'
                                        : '4px solid #f44336')
                                : (status === 1
                                    ? '4px solid #2e7d32'
                                    : status === 2
                                        ? '4px solid #f57c00'
                                        : '4px solid #d32f2f'),
                        backgroundColor: isDisabled
                            ? (darkMode ? '#2a2a2a' : '#f5f5f5')
                            : 'transparent',
                    };

                    return (
                        <React.Fragment>
                            {/* Чекбокс управления isDisabled (основной элемент) */}
                            <TableCell sx={cellStyle} align="center">
                                <Checkbox
                                    size="small"
                                    checked={!isDisabled}
                                    sx={{
                                        padding: '2px',
                                        color: darkMode ? '#64b5f6' : '#1976d2',
                                        '&.Mui-checked': {
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                        },
                                        '&.MuiCheckbox-indeterminate': {
                                            color: darkMode ? '#64b5f6' : '#1976d2',
                                        },
                                        '&:hover': {
                                            backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                        }
                                    }}
                                    onChange={(e) => {
                                        const checked = e.target.checked;
                                        
                                        // Обновляем isDisabled через disabledObjects для конкретного сегмента
                                        if (checked) {
                                            // Галочка поставлена - убираем из disabled
                                            setDisabledObjects(prev => ({
                                                ...prev,
                                                fiders: (prev.fiders || []).filter(id => id !== segmentKey)
                                            }));
                                        } else {
                                            // Галочка снята - добавляем в disabled
                                            setDisabledObjects(prev => ({
                                                ...prev,
                                                fiders: [...(prev.fiders || []).filter(id => id !== segmentKey), segmentKey]
                                            }));
                                        }
                                        
                                        // Вычисляем новый status на основе нового isDisabled и режима
                                        const newStatus = mode 
                                            ? (checked ? 1 : 0)  // Режим выполнения: checked = 1, unchecked = 0
                                            : (checked ? 0 : 1); // Режим отмены: checked = 0, unchecked = 1
                                        
                                        // Обновляем status сегмента
                                        setChangedObjectsFiders(prev => {
                                            // Если в режиме выполнения и снимаем галочку (status = 0), сбрасываем изменения сегмента
                                            if (mode && !checked) {
                                                // Сбрасываем к исходным значениям
                                                return {
                                                    ...prev,
                                                    [segmentKey]: {
                                                        length: originalSegment?.length ?? segment.length,
                                                        sip_class: originalSegment?.sip_class ?? segment.sip_class ?? [],
                                                        progress: originalSegment?.progress ?? 1,
                                                        status: 0
                                                    }
                                                };
                                            }
                                            
                                            // Обычное обновление статуса (сохраняем текущие изменения)
                                            const currentChanges = prev[segmentKey] || {};
                                            return {
                                                ...prev,
                                                [segmentKey]: {
                                                    ...currentChanges,
                                                    status: newStatus
                                                }
                                            };
                                        });
                                    }}
                                />
                            </TableCell>
                            {/* Кнопка перехода к фидеру на карте */}
                            <TableCell sx={cellStyle} align="center">
                                <LocationOnIcon
                                    fontSize="small"
                                    style={{
                                        color: isDisabled ? (darkMode ? '#666' : '#999') : 'inherit',
                                        cursor: isDisabled ? 'not-allowed' : 'pointer'
                                    }}
                                    onClick={() => {
                                        if (!isDisabled) {
                                            selectObjectFider(segment);
                                        }
                                    }}
                                />
                            </TableCell>
                            {/* Объект 1 */}
                            <TableCell sx={cellStyle} align="center">
                                <Typography
                                    onClick={(e) => {
                                        if (!isDisabled) {
                                            selectObject(leftObject);
                                        }
                                        e.stopPropagation();
                                    }}
                                    sx={{
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        fontSize: 'inherit',
                                        color: isDisabled ? (darkMode ? '#666' : '#999') : 'inherit'
                                    }}
                                >
                                    {/* {leftObject?.name || "Не определено"} */}
                                    {leftObject?.type?.includes('pillar')
                                        ? (
                                            <>
                                                {`${leftObject?.short_name} (${leftObject?.mount?.name?.split(' ')[0] || ''})`}
                                                <br />
                                                {leftObject?.mount?.category || ''}
                                            </>
                                        )
                                        : leftObject?.name}
                                </Typography>
                            </TableCell>
                            {/* Объект 2 */}
                            <TableCell sx={cellStyle} align="center">
                                <Typography
                                    onClick={(e) => {
                                        if (!isDisabled) {
                                            selectObject(rightObject);
                                        }
                                        e.stopPropagation();
                                    }}
                                    sx={{
                                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                                        fontSize: 'inherit',
                                        color: isDisabled ? (darkMode ? '#666' : '#999') : 'inherit'
                                    }}
                                >
                                    {/* {rightObject?.name || "Не определено"} */}
                                    {rightObject?.type?.includes('pillar')
                                        ? (
                                            <>
                                                {`${rightObject?.short_name} (${rightObject?.mount?.name?.split(' ')[0] || ''})`}
                                                <br />
                                                {rightObject?.mount?.category || ''}
                                            </>
                                        )
                                        : rightObject?.name}
                                </Typography>
                            </TableCell>

                            {/* Расширенные колонки для полного режима */}
                            {!smallMode && (
                                <>
                                    {/* Количество цепей */}
                                    {/* <TableCell sx={cellStyle} align="center">
                                        {mode ? (
                                            <FormControl size="small" disabled={isDisabled}>
                                                <Select
                                                    value={f.mountedQuantity || 0}
                                                    onChange={(e) => {
                                                        if (isDisabled || !mode) return;

                                                        const fiderId = `${f.fid}-${f.object_1_fid}-${f.object_2_fid}`;
                                                        let mounted;

                                                        if (object1?.type === 'pillar04-10' && object2?.type === 'pillar04-10') {
                                                            let c = 0
                                                            mounted = f.mounted.map((m, i) => {
                                                                if ((c + m.quantity) <= e.target.value) {
                                                                    c += m.quantity
                                                                    return { ...m, quantity: m.quantity }
                                                                } else {
                                                                    if (c < e.target.value) {
                                                                        return { ...m, quantity: e.target.value - c }
                                                                    } else {
                                                                        return null
                                                                    }
                                                                }
                                                            }).filter(m => m !== null)
                                                            if (e.target.value > 0 && mounted.length === 0) {
                                                                mounted.push({ ...f.spec, quantity: e.target.value })
                                                            }
                                                        } else {
                                                            mounted = [{ ...(f.mounted[0] || f.spec), quantity: e.target.value }]
                                                        }

                                                        // Обновляем только состояние изменений
                                                        setChangedObjectsFiders(prev => ({
                                                            ...prev,
                                                            [fiderId]: {
                                                                ...prev[fiderId],
                                                                mountedQuantity: e.target.value,
                                                                mounted: mounted
                                                            }
                                                        }));
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    size="small"
                                                    disabled={!mode}

                                                    sx={{
                                                        minWidth: '32px',
                                                        height: '32px',
                                                        padding: '0px 2px',
                                                        minHeight: '32px',
                                                        '& .MuiSelect-select': {
                                                            color: f.mountedQuantity === f.quantity
                                                                ? (darkMode ? '#f5f7fa' : 'inherit')
                                                                : (darkMode ? '#ff9800' : '#f57c00'),
                                                            fontSize: '0.75rem',
                                                            fontWeight: 400,
                                                        },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                                        },
                                                        backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                                        '& .MuiSelect-icon': {
                                                            color: darkMode ? '#f5f7fa' : '#333',
                                                        },
                                                    }}

                                                    MenuProps={{
                                                        container: dialogRef.current,
                                                        PaperProps: {
                                                            sx: {
                                                                zIndex: 9999,
                                                                backgroundColor: darkMode ? '#2b2b2b' : '#fff',
                                                                color: darkMode ? '#f5f7fa' : '#333',
                                                                '& .MuiMenuItem-root': {
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 400,
                                                                    padding: '2px 4px',
                                                                    minHeight: '24px',
                                                                    // color: darkMode ? '#f5f7fa' : '#333',
                                                                    '&:hover': {
                                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                                                    },
                                                                    '&.Mui-selected': {
                                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                                                                    }
                                                                }
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <MenuItem key={1} value={1} sx={{ color: f.quantity === 1 ? (darkMode ? '#f5f7fa' : '#333') : (darkMode ? '#ff9800' : '#f57c00') }}>1 цепь</MenuItem>
                                                    <MenuItem key={2} value={2} sx={{ color: f.quantity === 2 ? (darkMode ? '#f5f7fa' : '#333') : (darkMode ? '#ff9800' : '#f57c00') }}>2 цепи</MenuItem>
                                                    <MenuItem key={3} value={3} sx={{ color: f.quantity === 3 ? (darkMode ? '#f5f7fa' : '#333') : (darkMode ? '#ff9800' : '#f57c00') }}>3 цепи</MenuItem>
                                                    <MenuItem key={4} value={4} sx={{ color: f.quantity === 4 ? (darkMode ? '#f5f7fa' : '#333') : (darkMode ? '#ff9800' : '#f57c00') }}>4 цепи</MenuItem>
                                                </Select>
                                            </FormControl>
                                        ) : (
                                            <Typography sx={{ fontSize: 'inherit' }}>
                                                {f.mountedQuantity}
                                            </Typography>
                                        )}
                                    </TableCell> */}

                                    {/* Длина пролета */}
                                    <TableCell sx={cellStyle} align="center">
                                        {mode ? (
                                            <FormControl size="small" disabled={isDisabled}>
                                                <OutlinedInput
                                                    type="number"
                                                    size="small"
                                                    value={segmentLength}
                                                    onChange={(e) => {
                                                        if (isDisabled || !mode) return;

                                                        const newLength = parseFloat(e.target.value) || 0;

                                                        // Вычисляем новый progress на основе новой длины
                                                        // distance - это референсное значение (расстояние между координатами)
                                                        const referenceDistance = segment.distance || originalSegment?.length || segment.length || 0;
                                                        const newProgress = referenceDistance > 0 
                                                            ? Math.round((newLength / referenceDistance) * 100) / 100
                                                            : 1;

                                                        // Обновляем данные сегмента (length и progress)
                                                        setChangedObjectsFiders(prev => {
                                                            const currentChanges = prev[segmentKey] || {};
                                                            return {
                                                                ...prev,
                                                                [segmentKey]: {
                                                                    ...currentChanges,
                                                                    length: newLength,
                                                                    progress: newProgress
                                                                }
                                                            };
                                                        });
                                                    }}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    endAdornment={<InputAdornment position="end" sx={{
                                                        padding: '0px 2px',
                                                        margin: '0px',
                                                        color: darkMode ? '#f5f7fa' : '#333',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 400,
                                                    }}>м</InputAdornment>}
                                                    error={segmentLength === 0}
                                                    inputProps={{
                                                        step: "0.1",
                                                        min: "0"
                                                    }}
                                                    sx={{
                                                        width: '110px',
                                                        height: '32px',
                                                        '& .MuiInputBase-input': {
                                                            color: parseFloat(originalSegmentLength) === parseFloat(segmentLength)
                                                                ? (darkMode ? '#f5f7fa' : 'inherit')
                                                                : (darkMode ? '#ff9800' : '#f57c00'),
                                                            fontSize: '0.75rem',
                                                            fontWeight: 400,
                                                        },
                                                        '& .MuiTypography-root': {
                                                            color: darkMode ? '#f5f7fa' : '#333',
                                                        },
                                                        '&.Mui-error .MuiInputBase-input': {
                                                            color: darkMode ? '#f44336' : '#d32f2f',
                                                        },
                                                        '& input': {
                                                            padding: '4px 8px'
                                                        },
                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                                        },
                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                        },
                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                            borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                                        },
                                                        backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                                    }}
                                                />
                                            </FormControl>
                                        ) : (
                                            <Typography sx={{ fontSize: 'inherit' }}>
                                                {segmentLength.toFixed(2)} м
                                            </Typography>
                                        )}
                                    </TableCell>

                                    {/* Общая длина провода */}
                                    <TableCell sx={cellStyle} align="center">
                                        <Typography sx={{ fontSize: 'inherit' }}>
                                            {(() => {
                                                // Длина провода для текущего сегмента = длина сегмента × количество спецификаций
                                                const segmentSipClassCount = Array.isArray(segmentSipClass) ? segmentSipClass.length : 1;
                                                const totalLength = segmentLength * segmentSipClassCount;
                                                return totalLength.toFixed(2).replace('.', ',') + ' м';
                                            })()}
                                        </Typography>
                                    </TableCell>

                                    {/* Спецификация */}
                                    <TableCell sx={cellStyle} align="center">
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', alignItems: 'center' }}>
                                            {segmentSipClass.map((sipClassId, index) => {
                                                const spec = specs?.find(s => s.id === sipClassId);
                                                
                                                // Получаем исходную спецификацию для сравнения
                                                const originalSipClass = originalSegment?.sip_class || segment.sip_class || [];
                                                const originalValue = originalSipClass[index];
                                                const isSpecChanged = originalValue != null 
                                                    ? Number(originalValue) !== Number(sipClassId)
                                                    : sipClassId !== null && sipClassId !== undefined;
                                                
                                                return (
                                                    <Box key={`box-${segment.fid}-${segment.key}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
                                                        {mode && (
                                                            <FormControl size="small" disabled={isDisabled}>
                                                                <Select
                                                                    value={sipClassId || ''}
                                                                    onChange={(e) => {
                                                                        if (isDisabled || !mode) return;

                                                                        const updatedSipClass = [...segmentSipClass];
                                                                        if (updatedSipClass[index] !== undefined) {
                                                                            updatedSipClass[index] = parseInt(e.target.value);
                                                                        } else {
                                                                            updatedSipClass.push(parseInt(e.target.value));
                                                                        }

                                                                        // Обновляем данные сегмента
                                                                        setChangedObjectsFiders(prev => {
                                                                            const currentChanges = prev[segmentKey] || {};
                                                                            return {
                                                                                ...prev,
                                                                                [segmentKey]: {
                                                                                    ...currentChanges,
                                                                                    sip_class: updatedSipClass
                                                                                }
                                                                            };
                                                                        });
                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                    size="small"
                                                                    disabled={!mode}
                                                                    sx={{
                                                                        minWidth: '200px',
                                                                        height: '32px',
                                                                        padding: '0px 2px',
                                                                        minHeight: '32px',
                                                                        '& .MuiSelect-select': {
                                                                            color: isSpecChanged
                                                                                ? (darkMode ? '#ff9800' : '#f57c00')
                                                                                : (spec
                                                                                    ? (darkMode ? '#f5f7fa' : 'inherit')
                                                                                    : (darkMode ? '#ff9800' : '#f57c00')),
                                                                            fontSize: '0.75rem',
                                                                            fontWeight: 400,
                                                                        },
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                                                        },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                                                        },
                                                                        backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                                                        '& .MuiSelect-icon': {
                                                                            color: darkMode ? '#f5f7fa' : '#333',
                                                                        },
                                                                    }}
                                                                    MenuProps={{
                                                                        disablePortal: true,
                                                                        container: dialogRef.current,
                                                                        PaperProps: {
                                                                            sx: {
                                                                                zIndex: 9999,
                                                                                backgroundColor: darkMode ? '#2b2b2b' : '#fff',
                                                                                color: darkMode ? '#f5f7fa' : '#333',
                                                                                '& .MuiMenuItem-root': {
                                                                                    fontSize: '0.8rem',
                                                                                    padding: '2px 4px',
                                                                                    minHeight: '24px',
                                                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                                                    '&:hover': {
                                                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                                                                    },
                                                                                    '&.Mui-selected': {
                                                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    {[...specs]?.sort((a, b) => a.name_str.localeCompare(b.name_str)).map(t => (
                                                                        <MenuItem key={t.id} value={t.id}>{t.name_str}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        )}
                                                        {!mode && (
                                                            <Typography sx={{ fontSize: 'inherit' }}>
                                                                {spec?.name_str || 'Не указано'}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )
                                            })}
                                            {(() => {
                                                // Для combined фидеров должно быть 2 класса сипа, для остальных 1
                                                const expectedCount = isCombined ? 2 : 1;
                                                if (segmentSipClass.length === 0) {
                                                    return (
                                                        <Typography sx={{ fontSize: 'inherit', color: darkMode ? '#999' : '#666' }}>
                                                            Не указано
                                                        </Typography>
                                                    );
                                                }
                                                // Если для combined не хватает второго класса, показываем возможность добавить
                                                if (isCombined && segmentSipClass.length < 2 && mode) {
                                                    return (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            disabled={isDisabled}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (isDisabled || !mode) return;

                                                                // Добавляем пустое значение для выбора класса сипа
                                                                const updatedSipClass = [...segmentSipClass, (specs?.[0]?.id || null)];

                                                                setChangedObjectsFiders(prev => {
                                                                    const currentChanges = prev[segmentKey] || {};
                                                                    return {
                                                                        ...prev,
                                                                        [segmentKey]: {
                                                                            ...currentChanges,
                                                                            sip_class: updatedSipClass
                                                                        }
                                                                    };
                                                                });
                                                            }}
                                                            sx={{
                                                                fontSize: '0.7rem',
                                                                padding: '2px 8px',
                                                                minHeight: '24px',
                                                            }}
                                                        >
                                                            + Добавить класс СИП
                                                        </Button>
                                                    );
                                                }
                                                return null;
                                            })()}
                                            {/* {f.mounted.map((m, index) => {
                                                const isValidId = f.allowedSpecs.find(tf => tf.id === m.id);
                                                console.log(m);
                                                console.log(object1, object2);
                                                return (
                                                    <Box key={`box-${f.fid}-${f.object_1_fid}-${f.object_2_fid}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 1 }}>
                                                        
                                                        {mode && (
                                                            <FormControl size="small" disabled={f.mountedQuantity * f.mountedDistance === 0 || isDisabled}>
                                                                <Select
                                                                    value={isValidId ? m.id : ''}
                                                                    error={!isValidId}
                                                                    onChange={(e) => {
                                                                        if (isDisabled || !mode) return;

                                                                        const fiderId = `${f.fid}-${f.object_1_fid}-${f.object_2_fid}`;
                                                                        const updatedMounted = f.mounted.map((mount, idx) => {
                                                                            if (idx === index) {
                                                                                return { ...f.allowedSpecs.find(s => s.id === e.target.value), quantity: m.quantity };
                                                                            }
                                                                            return mount;
                                                                        });

                                                                        // Обновляем только состояние изменений
                                                                        setChangedObjectsFiders(prev => ({
                                                                            ...prev,
                                                                            [fiderId]: {
                                                                                ...prev[fiderId],
                                                                                mounted: updatedMounted
                                                                            }
                                                                        }));

                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                    size="small"
                                                                    disabled={!mode}
                                                                    sx={{
                                                                        minWidth: '200px',
                                                                        height: '32px',
                                                                        padding: '0px 2px',
                                                                        minHeight: '32px',
                                                                        '& .MuiSelect-select': {
                                                                            color: f.spec.id === m.id
                                                                                ? (darkMode ? '#f5f7fa' : 'inherit')
                                                                                : (darkMode ? '#ff9800' : '#f57c00'),
                                                                            fontSize: '0.75rem',
                                                                            fontWeight: 400,
                                                                        },
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                                                        },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                                                        },
                                                                        backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                                                        '& .MuiSelect-icon': {
                                                                            color: darkMode ? '#f5f7fa' : '#333',
                                                                        },
                                                                    }}
                                                                    MenuProps={{
                                                                        disablePortal: true,
                                                                        container: dialogRef.current,
                                                                        PaperProps: {
                                                                            sx: {
                                                                                zIndex: 9999,
                                                                                backgroundColor: darkMode ? '#2b2b2b' : '#fff',
                                                                                color: darkMode ? '#f5f7fa' : '#333',
                                                                                '& .MuiMenuItem-root': {
                                                                                    fontSize: '0.8rem',
                                                                                    padding: '2px 4px',
                                                                                    minHeight: '24px',
                                                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                                                    '&:hover': {
                                                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                                                                    },
                                                                                    '&.Mui-selected': {
                                                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    {f.allowedSpecs.map(t => (
                                                                        <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        )}

                                                        //Селектор количества только для опор совместного подвеса
                                                        {mode && combined && (
                                                            <FormControl size="small" disabled={isDisabled}>
                                                                <Select
                                                                    value={m.quantity}
                                                                    onChange={(e) => {
                                                                        if (isDisabled || !mode) return;

                                                                        const fiderId = `${f.fid}-${f.object_1_fid}-${f.object_2_fid}`;
                                                                        const updatedMounted = f.mounted.map((mount, idx) => {
                                                                            if (idx === index) {
                                                                                return { ...mount, quantity: e.target.value };
                                                                            }
                                                                            return mount;
                                                                        });

                                                                        // Обновляем только состояние изменений
                                                                        setChangedObjectsFiders(prev => ({
                                                                            ...prev,
                                                                            [fiderId]: {
                                                                                ...prev[fiderId],
                                                                                mounted: updatedMounted
                                                                            }
                                                                        }));

                                                                    }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                    }}
                                                                    sx={{
                                                                        fontSize: '14px',
                                                                        height: '32px',
                                                                        padding: '0px 2px',
                                                                        minHeight: '32px',
                                                                        '& .MuiSelect-select': {
                                                                            color: darkMode ? '#f5f7fa' : '#333',
                                                                            fontSize: '0.75rem',
                                                                            fontWeight: 400,
                                                                        },
                                                                        '&.Mui-error .MuiSelect-select': {
                                                                            color: darkMode ? '#f44336' : '#d32f2f',
                                                                        },
                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.23)' : 'rgba(0,0,0,0.23)',
                                                                        },
                                                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)',
                                                                        },
                                                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                                            borderColor: darkMode ? '#64b5f6' : '#1976d2',
                                                                        },
                                                                        backgroundColor: darkMode ? '#1e1e1e' : '#f8f9fa',
                                                                        '& .MuiSelect-icon': {
                                                                            color: darkMode ? '#f5f7fa' : '#333',
                                                                        },
                                                                    }}
                                                                    MenuProps={{
                                                                        container: dialogRef.current,
                                                                        PaperProps: {
                                                                            sx: {
                                                                                zIndex: 9999,
                                                                                backgroundColor: darkMode ? '#2b2b2b' : '#fff',
                                                                                color: darkMode ? '#f5f7fa' : '#333',
                                                                                '& .MuiMenuItem-root': {
                                                                                    fontSize: '0.8rem',
                                                                                    padding: '2px 4px',
                                                                                    minHeight: '24px',
                                                                                    color: darkMode ? '#f5f7fa' : '#333',
                                                                                    '&:hover': {
                                                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                                                                                    },
                                                                                    '&.Mui-selected': {
                                                                                        backgroundColor: darkMode ? 'rgba(100, 181, 246, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                    }}

                                                                >
                                                                    {[...Array(f.mountedQuantity - f.mounted.reduce((sum, n) => sum + (m.id !== n.id ? n.quantity : 0), 0)).keys()].map(i => (
                                                                        <MenuItem key={i + 1} value={i + 1}>{i + 1} шт</MenuItem>
                                                                    ))}
                                                                </Select>
                                                            </FormControl>
                                                        )}


                                                        {!mode
                                                            && (object1?.type?.includes('pillar') ? object1?.mounted?.combined : true)
                                                            && (object2?.type?.includes('pillar') ? object2?.mounted?.combined : true)
                                                            && f.mountedQuantity >= 1
                                                            && (
                                                                <Typography sx={{ fontSize: 'inherit', minWidth: 80 }}>
                                                                    {m.quantity} шт
                                                                </Typography>
                                                            )}

                                                        // Кнопка добавления
                                                        {mode && combined && index === f.mounted.length - 1 && f.mounted.reduce((sum, m) => sum + m.quantity, 0) < f.mountedQuantity && (
                                                            <Button
                                                                key={`${f.fid}-${f.object_1_fid}-${f.object_2_fid}-add`}
                                                                variant="contained"
                                                                color="success"
                                                                size="small"
                                                                ref={addButtonRef}
                                                                disabled={isDisabled}
                                                                sx={{
                                                                    minWidth: '32px',
                                                                    height: '32px',
                                                                    padding: '0px 2px',
                                                                    minHeight: '32px',
                                                                    backgroundColor: darkMode ? '#4caf50' : '#4caf50',
                                                                    color: '#fff',
                                                                    '&:hover': {
                                                                        backgroundColor: darkMode ? '#45a049' : '#45a049',
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5',
                                                                        color: darkMode ? '#666' : '#999',
                                                                    }
                                                                }}
                                                                onClick={() => {
                                                                    if (isDisabled) return;

                                                                    const fiderId = `${f.fid}-${f.object_1_fid}-${f.object_2_fid}`;
                                                                    const newMountedItem = {
                                                                        ...f.allowedSpecs.find(s => !f.mounted.map(n => n.id).includes(s.id)),
                                                                        quantity: f.mountedQuantity - f.mounted.reduce((sum, n) => sum + n.quantity, 0)
                                                                    };
                                                                    const updatedMounted = [...f.mounted, newMountedItem];

                                                                    // Обновляем только состояние изменений
                                                                    setChangedObjectsFiders(prev => ({
                                                                        ...prev,
                                                                        [fiderId]: {
                                                                            ...prev[fiderId],
                                                                            mounted: updatedMounted
                                                                        }
                                                                    }));

                                                                }}
                                                            >
                                                                +
                                                            </Button>
                                                        )}

                                                        // Кнопка удаления 
                                                        {mode && combined && index === f.mounted.length - 1 && f.mounted.length > 1 && (
                                                            <Button
                                                                key={`${f.fid}-${f.object_1_fid}-${f.object_2_fid}-remove`}
                                                                variant="contained"
                                                                color="error"
                                                                size="small"
                                                                disabled={isDisabled}
                                                                sx={{
                                                                    minWidth: '32px',
                                                                    height: '32px',
                                                                    padding: '0px 2px',
                                                                    minHeight: '32px',
                                                                    backgroundColor: darkMode ? '#f44336' : '#f44336',
                                                                    color: '#fff',
                                                                    '&:hover': {
                                                                        backgroundColor: darkMode ? '#d32f2f' : '#d32f2f',
                                                                    },
                                                                    '&.Mui-disabled': {
                                                                        backgroundColor: darkMode ? '#2a2a2a' : '#f5f5f5',
                                                                        color: darkMode ? '#666' : '#999',
                                                                    }
                                                                }}
                                                                onClick={() => {
                                                                    if (isDisabled) return;

                                                                    const fiderId = `${f.fid}-${f.object_1_fid}-${f.object_2_fid}`;
                                                                    const updatedMounted = f.mounted.filter((_, idx) => idx !== index);

                                                                    // Обновляем только состояние изменений
                                                                    setChangedObjectsFiders(prev => ({
                                                                        ...prev,
                                                                        [fiderId]: {
                                                                            ...prev[fiderId],
                                                                            mounted: updatedMounted
                                                                        }
                                                                    }));

                                                                }}
                                                            >
                                                                -
                                                            </Button>
                                                        )}
                                                    </Box>
                                                );
                                            })} */}
                                            {/* Предупреждение о недостающих проводах */}
                                            {/* {mode && combined && f.mountedQuantity > f.mounted.reduce((sum, m) => sum + m.quantity, 0) && (
                                                <Alert
                                                    severity="warning"
                                                    variant="outlined"
                                                    sx={{
                                                        fontSize: '12px',
                                                        padding: '2px 2px',
                                                        maxWidth: 'auto',
                                                        '& .MuiAlert-icon': {
                                                            margin: '1px',
                                                            padding: '0px 2px'
                                                        },
                                                        '& .MuiAlert-message': {
                                                            alignSelf: 'center',
                                                            padding: '0px 2px'
                                                        }
                                                    }}
                                                >
                                                    Необходимо добавить проводов: <b>{f.mountedQuantity - f.mounted.reduce((sum, m) => sum + m.quantity, 0)}</b>
                                                </Alert>
                                            )} */}
                                        </Box>
                                    </TableCell>
                                </>
                            )}
                        </React.Fragment>
                    );
                }}
            />
        </Box>
    );
});

export default ObjectsFidersTable;
